import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Api from 'api';
import useActionApi from 'hooks/useActionApi';

import {
  ActionButton,
  Actions,
  Advisory,
  CardContent,
  CardFieldset,
  CardGrid,
  ComposeMeta,
  Draft,
  DraftLabel,
  EditButton,
  ErrorMessage,
  Header,
  LoadingPanel,
  PreviewButton,
  RadioLabel,
  RedactionList,
  SaveAsBase,
  Shell,
  SkipButton,
  VersionCard,
  VersionEditor,
  VersionPreview,
  VisuallyHiddenLegend,
} from './WritingAssist.styles';
import {
  isWritingAssistSuccess,
  WritingAssistProps,
  WritingAssistRequest,
  WritingAssistResponse,
  WritingAssistSuccessResponse,
  WritingAssistVersion,
} from './WritingAssist.types';
import {
  COMPOSE_COPY,
  contextCopy,
  countWords,
  detectContactInfo,
  FAILURE_COPY,
  REVIEW_COPY,
  usageCopy,
} from './writingAssistCopy';

const CARD_COPY: Record<WritingAssistVersion, { title: string; tag: string }> = {
  original: { title: 'Your original', tag: 'as typed' },
  tidied: { title: 'Tidied', tag: 'sounds like you' },
  enhanced: { title: 'Enhanced', tag: 'same facts, more shine' },
};

const REDACTION_COPY = {
  contact_phone: '☒ phone number removed',
  contact_email: '☒ email address removed',
  contact_url: '☒ link removed',
  contact_social: '☒ social handle removed',
} as const;

function writerErrorMessage(error: any): string {
  if (error?.status === 429 && error?.body?.limit?.message) {
    return String(error.body.limit.message);
  }
  if (error?.status === 429) return FAILURE_COPY.burst;
  return FAILURE_COPY.writer;
}

const WritingAssist: FC<WritingAssistProps> = ({
  context,
  initialText = '',
  entityId,
  firstName,
  practiceName,
  variant = 'profile',
  className,
  requestWritingAssist = Api.requestWritingAssist,
  onTextChange,
  onSave,
  onSkip,
  onWriterResult,
  onGateResult,
  onSaveError,
  autoPolishRequest = 0,
}) => {
  const copy = useMemo(
    () => contextCopy({ context, variant, firstName, practiceName }),
    [context, firstName, practiceName, variant],
  );
  const [draft, setDraft] = useState(initialText);
  const [step, setStep] = useState<'compose' | 'review'>('compose');
  const [result, setResult] = useState<WritingAssistSuccessResponse | null>(null);
  const [usage, setUsage] = useState<WritingAssistSuccessResponse['assists'] | null>(null);
  const [selected, setSelected] = useState<WritingAssistVersion | null>(null);
  const [expandedPreviews, setExpandedPreviews] = useState<Partial<Record<WritingAssistVersion, boolean>>>({});
  const [versions, setVersions] = useState<Record<WritingAssistVersion, string>>({
    original: initialText,
    tidied: '',
    enhanced: '',
  });
  const [saveAsBase, setSaveAsBase] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [writerError, setWriterError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadingCopy, setLoadingCopy] = useState<string>(COMPOSE_COPY.loadingRead);
  const requestEpoch = useRef(0);
  const requestInFlight = useRef(false);
  const saveEpoch = useRef(0);
  const saveInFlight = useRef(false);
  const activeSuccess = useRef<(response: WritingAssistResponse) => void>(() => undefined);
  const activeError = useRef<(error: unknown) => void>(() => undefined);
  const editorRefs = useRef<Partial<Record<WritingAssistVersion, HTMLTextAreaElement | null>>>({});
  const seenAutoPolishRequest = useRef(0);

  const apiMethod = useCallback(
    (request: WritingAssistRequest) => requestWritingAssist(request),
    [requestWritingAssist],
  );
  const [writerLoading, , makeWriterRequest] = useActionApi<WritingAssistRequest, WritingAssistResponse>({
    apiMethod,
    onSuccess: response => activeSuccess.current(response),
    onError: error => activeError.current(error),
  });

  useEffect(() => {
    requestEpoch.current += 1;
    requestInFlight.current = false;
    saveEpoch.current += 1;
    saveInFlight.current = false;
    setDraft(initialText);
    setStep('compose');
    setResult(null);
    setUsage(null);
    setSelected(null);
    setExpandedPreviews({});
    setVersions({ original: initialText, tidied: '', enhanced: '' });
    setSaveAsBase(false);
    setSelectionError(null);
    setWriterError(null);
    setSaveLoading(false);
    return () => {
      requestEpoch.current += 1;
      saveEpoch.current += 1;
    };
  }, [context, entityId, initialText, variant]);

  useEffect(() => {
    if (!writerLoading) return undefined;
    setLoadingCopy(COMPOSE_COPY.loadingRead);
    const timer = window.setTimeout(() => setLoadingCopy(COMPOSE_COPY.loadingPrepare), 600);
    return () => window.clearTimeout(timer);
  }, [writerLoading]);

  const wordCount = countWords(draft);
  const meetsMinimum = wordCount >= copy.minWords;
  const contact = detectContactInfo(draft);
  const busy = writerLoading || saveLoading;

  const handleDraftChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value;
    setDraft(next);
    setWriterError(null);
    onTextChange?.(next);
  };

  const handlePolish = () => {
    if (!meetsMinimum || requestInFlight.current || saveInFlight.current) return;
    const epoch = ++requestEpoch.current;
    requestInFlight.current = true;
    setWriterError(null);
    setSelectionError(null);

    activeSuccess.current = response => {
      requestInFlight.current = false;
      if (requestEpoch.current !== epoch) return;
      onWriterResult?.(response);
      if (!isWritingAssistSuccess(response)) {
        if ('refusal' in response && typeof response.refusal?.message === 'string') {
          setWriterError(response.refusal.message);
        } else {
          setWriterError(FAILURE_COPY.writer);
        }
        return;
      }
      setResult(response);
      setVersions({ original: draft, tidied: response.tidied, enhanced: response.enhanced });
      // Store a fresh snapshot from the latest successful response only. Never debit or persist it locally.
      setUsage({ ...response.assists });
      setSelected(null);
      setExpandedPreviews({});
      setSaveAsBase(false);
      setStep('review');
    };
    activeError.current = error => {
      requestInFlight.current = false;
      if (requestEpoch.current !== epoch) return;
      setWriterError(writerErrorMessage(error));
    };

    const request: WritingAssistRequest = { context, text: draft };
    if (entityId !== undefined) request.entityId = entityId;
    makeWriterRequest(request);
  };

  useEffect(() => {
    if (!autoPolishRequest || seenAutoPolishRequest.current === autoPolishRequest) return;
    seenAutoPolishRequest.current = autoPolishRequest;
    handlePolish();
    // The token is the command boundary. Including handlePolish would rerun
    // this effect on every render while a request is in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPolishRequest]);

  const performSave = async (version: WritingAssistVersion, text: string) => {
    if (saveInFlight.current) return;
    const epoch = ++saveEpoch.current;
    saveInFlight.current = true;
    const hash = version === 'original' ? undefined : result?.hashes[version];
    setSaveLoading(true);
    setSelectionError(null);
    try {
      const gateResult = await onSave({
        text,
        version,
        hash,
        saveAsBase: context === 'applicationCoverLetter' ? saveAsBase : undefined,
      });
      if (saveEpoch.current === epoch && gateResult !== undefined) onGateResult?.(gateResult);
    } catch (error) {
      if (saveEpoch.current === epoch) onSaveError?.(error);
    } finally {
      if (saveEpoch.current === epoch) {
        saveInFlight.current = false;
        setSaveLoading(false);
      }
    }
  };

  const handleSaveAsIs = () => {
    if (!meetsMinimum || requestInFlight.current || saveInFlight.current) return;
    void performSave('original', draft);
  };

  const selectVersion = (version: WritingAssistVersion) => {
    setSelected(version);
    setExpandedPreviews({});
    setSaveAsBase(false);
    setSelectionError(null);
  };

  const handleReviewSave = () => {
    if (!selected) {
      setSelectionError(REVIEW_COPY.noSelection);
      return;
    }
    void performSave(selected, versions[selected]);
  };

  const handleBack = () => {
    setStep('compose');
    setSelected(null);
    setExpandedPreviews({});
    setSaveAsBase(false);
    setSelectionError(null);
    setWriterError(null);
  };

  if (step === 'compose') {
    return (
      <Shell className={className} aria-busy={busy}>
        <Header>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </Header>
        <DraftLabel>
          {copy.title}
          <Draft
            aria-label={copy.title}
            value={draft}
            placeholder={copy.placeholder}
            onChange={handleDraftChange}
            disabled={busy}
          />
        </DraftLabel>
        <ComposeMeta>
          <span>{wordCount} {wordCount === 1 ? 'word' : 'words'}</span>
          {usage && <span aria-live="polite">{usageCopy(usage)}</span>}
        </ComposeMeta>
        {!meetsMinimum && <ErrorMessage>{COMPOSE_COPY.minWords(copy.minWords)}</ErrorMessage>}
        {contact.detected && <Advisory>{COMPOSE_COPY.contactInfo}</Advisory>}
        {writerError && <ErrorMessage role="alert">{writerError}</ErrorMessage>}
        {busy && (
          <LoadingPanel role="status" aria-live="polite">
            {saveLoading ? COMPOSE_COPY.loadingSave : loadingCopy}
          </LoadingPanel>
        )}
        <Actions>
          {copy.skipLabel && onSkip && (
            <SkipButton type="button" onClick={onSkip} disabled={busy}>{copy.skipLabel}</SkipButton>
          )}
          <ActionButton type="button" onClick={handleSaveAsIs} disabled={!meetsMinimum || busy}>
            {COMPOSE_COPY.saveAsIs}
          </ActionButton>
          <ActionButton $primary type="button" onClick={handlePolish} disabled={!meetsMinimum || busy}>
            {COMPOSE_COPY.polish}
          </ActionButton>
        </Actions>
      </Shell>
    );
  }

  const violations = Array.from(new Set((result?.policy.violations || []).map(item => item.category)));
  const versionOrder: WritingAssistVersion[] = ['original', 'tidied', 'enhanced'];

  return (
    <Shell className={className} aria-busy={saveLoading}>
      <Header>
        <h2>{REVIEW_COPY.title}</h2>
        <p>{REVIEW_COPY.subtitle}</p>
      </Header>
      {usage && <ComposeMeta><span aria-live="polite">{usageCopy(usage)}</span></ComposeMeta>}
      <CardFieldset>
        <VisuallyHiddenLegend>{REVIEW_COPY.title}</VisuallyHiddenLegend>
        <CardGrid data-testid="writing-assist-card-grid" data-responsive-cards="vertical-stack">
          {versionOrder.map(version => {
            const card = CARD_COPY[version];
            const isSelected = selected === version;
            const isPreviewExpanded = Boolean(expandedPreviews[version]);
            const label = `${card.title} — ${card.tag}`;
            return (
              <VersionCard key={version} $selected={isSelected} data-selected={isSelected}>
                <RadioLabel htmlFor={`writing-assist-${version}`}>
                  <input
                    id={`writing-assist-${version}`}
                    type="radio"
                    name="writing-assist-version"
                    value={version}
                    aria-label={label}
                    checked={isSelected}
                    onClick={() => {
                      if (selected === version) setSaveAsBase(false);
                    }}
                    onChange={() => selectVersion(version)}
                    onKeyDown={event => {
                      if (event.key === ' ' || event.key === 'Enter') {
                        event.preventDefault();
                        selectVersion(version);
                      }
                    }}
                    disabled={saveLoading}
                  />
                  <span>
                    <strong>{card.title}</strong>
                    <span>{card.tag}</span>
                  </span>
                </RadioLabel>
                {version === 'original' && violations.length > 0 && (
                  <RedactionList aria-label="Contact details removed on save">
                    {violations.map(category => <li key={category}>{REDACTION_COPY[category]}</li>)}
                  </RedactionList>
                )}
                <CardContent>
                  {isSelected ? (
                    <VersionEditor
                      ref={node => { editorRefs.current[version] = node; }}
                      aria-label={`Edit ${card.title} version`}
                      value={versions[version]}
                      onChange={event => setVersions(current => ({ ...current, [version]: event.target.value }))}
                      disabled={saveLoading}
                    />
                  ) : (
                    <>
                      <VersionPreview
                        id={`writing-assist-${version}-preview`}
                        data-mobile-preview={isPreviewExpanded ? 'expanded' : 'collapsed'}
                      >
                        {versions[version]}
                      </VersionPreview>
                      <PreviewButton
                        type="button"
                        aria-controls={`writing-assist-${version}-preview`}
                        aria-expanded={isPreviewExpanded}
                        aria-label={`${isPreviewExpanded ? 'Show less' : 'View full'} ${card.title}`}
                        onClick={() => setExpandedPreviews(current => ({
                          ...current,
                          [version]: !current[version],
                        }))}
                        disabled={saveLoading}
                      >
                        {isPreviewExpanded ? 'Show less' : 'View full'}
                      </PreviewButton>
                    </>
                  )}
                </CardContent>
                {isSelected && (
                  <EditButton type="button" onClick={() => editorRefs.current[version]?.focus()} disabled={saveLoading}>
                    {REVIEW_COPY.edit}
                  </EditButton>
                )}
                {context === 'applicationCoverLetter' && isSelected && (
                  <SaveAsBase>
                    <input
                      type="checkbox"
                      checked={saveAsBase}
                      onChange={event => setSaveAsBase(event.target.checked)}
                      disabled={saveLoading}
                    />
                    <span>Use as my new Base Cover Letter</span>
                  </SaveAsBase>
                )}
              </VersionCard>
            );
          })}
        </CardGrid>
      </CardFieldset>
      {selectionError && <ErrorMessage role="alert" aria-live="assertive">{selectionError}</ErrorMessage>}
      {saveLoading && <LoadingPanel role="status" aria-live="polite">{COMPOSE_COPY.loadingSave}</LoadingPanel>}
      <Actions>
        <ActionButton type="button" onClick={handleBack} disabled={saveLoading}>{REVIEW_COPY.back}</ActionButton>
        <ActionButton $primary type="button" onClick={handleReviewSave} disabled={saveLoading}>{REVIEW_COPY.save}</ActionButton>
      </Actions>
    </Shell>
  );
};

export default WritingAssist;
